import { useMemo, type CSSProperties } from 'react';
import { Link } from 'react-router';
import { Descriptions, Space, Tag, Typography } from 'antd';
import type { Company } from '@viq-admin/types';
import { crmSourceLabel } from '@viq-admin/screens';
import {
  EM_DASH,
  SectionHeader,
  formatDate,
  formatDateTime,
  space,
  useNarrowViewport,
} from '@viq-admin/ui';
import { ConferenceLink, UserLink } from '../../../components/EntityLinks';
import { LeadsMiniTable } from '../../../components/LeadsMiniTable';
import {
  ROLE_COLOR,
  crmFlagOn,
  enabledCrmLabels,
  summarizeConferences,
  summarizeInstalls,
  summarizeUsers,
} from './model';
import { Metric, MetricRow, SummaryCard } from './SummaryCard';
import {
  useCompanyConferences,
  useCompanyCrmConnections,
  useCompanyInstalls,
  useCompanyUsers,
} from './useCompanyHubData';

/**
 * The company page's Overview: four summary cards (users, conferences, CRM,
 * devices) that answer the everyday questions at a glance, each heading
 * opening its full tab; then the company's details and its latest leads.
 *
 * Every read names this company explicitly (see useCompanyHubData), so the
 * Overview is the same whatever "Operate as" is set to.
 */

/** Cards wrap to one column on a phone; desktop gets as many as fit. */
const CARD_GRID: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(240px, 100%), 1fr))',
  gap: space.lg,
};

export function CompanyOverviewTab({
  company,
  onOpenTab,
}: {
  company: Company;
  onOpenTab: (key: string) => void;
}) {
  const narrow = useNarrowViewport();
  return (
    <Space direction="vertical" size={space.xl} style={{ width: '100%' }}>
      <div style={CARD_GRID}>
        <UsersCard companyId={company.id} onOpen={() => onOpenTab('users')} />
        <ConferencesCard companyId={company.id} onOpen={() => onOpenTab('conferences')} />
        <CrmCard company={company} onOpen={() => onOpenTab('integrations')} />
        <DevicesCard companyId={company.id} onOpen={() => onOpenTab('devices')} />
      </div>

      <div data-testid="company-read-view">
        <SectionHeader>Details</SectionHeader>
        <Descriptions size="small" column={narrow ? 1 : 3}>
          {/* Its own row: a tenant domain is the one long value here, and in a
              third of a tablet-width page it wraps a few letters per line. */}
          <Descriptions.Item label="Entra Hint" span={narrow ? 1 : 3}>
            {company.entraHint || EM_DASH}
          </Descriptions.Item>
          <Descriptions.Item label="Created">{formatDate(company.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="Updated">{formatDate(company.updatedAt)}</Descriptions.Item>
          {company.archivedAt && (
            <Descriptions.Item label="Archived">{formatDate(company.archivedAt)}</Descriptions.Item>
          )}
        </Descriptions>
      </div>

      <div>
        <SectionHeader>Recent leads</SectionHeader>
        <LeadsMiniTable filter={{ companyId: company.id }} hide={['company']} />
      </div>
    </Space>
  );
}

function UsersCard({ companyId, onOpen }: { companyId: string; onOpen: () => void }) {
  const { users, isLoading, error, refetch } = useCompanyUsers(companyId);
  const s = useMemo(() => summarizeUsers(users), [users]);
  return (
    <SummaryCard
      title="Users"
      testId="hub-card-users"
      onOpen={onOpen}
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
    >
      {users.length === 0 ? (
        <Typography.Text type="secondary">No users yet</Typography.Text>
      ) : (
        <>
          <MetricRow>
            <Metric value={s.active} label="Active" />
            <Metric value={s.inactive} label="Inactive" />
          </MetricRow>
          {s.byRole.length > 0 && (
            <div style={{ marginBottom: space.sm }}>
              {s.byRole.map((r) => (
                <Tag key={r.role} color={ROLE_COLOR[r.role] ?? 'default'} style={{ marginBlock: 2 }}>
                  {r.label} {r.count}
                </Tag>
              ))}
            </div>
          )}
          <Typography.Text type="secondary">Last login: </Typography.Text>
          {s.lastLogin ? (
            <>
              <UserLink oid={s.lastLogin.oid} name={s.lastLogin.name} />
              <Typography.Text type="secondary"> · {formatDateTime(s.lastLogin.lastLoginAt)}</Typography.Text>
            </>
          ) : (
            <Typography.Text type="secondary">nobody has signed in yet</Typography.Text>
          )}
        </>
      )}
    </SummaryCard>
  );
}

function ConferencesCard({ companyId, onOpen }: { companyId: string; onOpen: () => void }) {
  const { conferences, isLoading, error, refetch } = useCompanyConferences(companyId);
  const s = useMemo(() => summarizeConferences(conferences), [conferences]);
  return (
    <SummaryCard
      title="Conferences"
      testId="hub-card-conferences"
      onOpen={onOpen}
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
    >
      {conferences.length === 0 ? (
        <Typography.Text type="secondary">No conferences granted yet</Typography.Text>
      ) : (
        <>
          <MetricRow>
            <Metric value={s.live} label="Live now" tone={s.live > 0 ? 'success' : undefined} />
            <Metric value={s.upcoming} label="Upcoming" />
            <Metric value={s.past} label="Past" />
          </MetricRow>
          {s.current ? (
            <div>
              <Typography.Text type="secondary">Live now: </Typography.Text>
              <ConferenceLink id={s.current.id} name={s.current.name} />
              <Typography.Text type="secondary"> · until {formatDate(s.current.endDate)}</Typography.Text>
            </div>
          ) : s.next ? (
            <div>
              <Typography.Text type="secondary">Next: </Typography.Text>
              <ConferenceLink id={s.next.id} name={s.next.name} />
              <Typography.Text type="secondary"> · starts {formatDate(s.next.startDate)}</Typography.Text>
            </div>
          ) : (
            <Typography.Text type="secondary">Nothing live or upcoming</Typography.Text>
          )}
        </>
      )}
    </SummaryCard>
  );
}

function CrmCard({ company, onOpen }: { company: Company; onOpen: () => void }) {
  const { connections, isLoading, error, refetch } = useCompanyCrmConnections(company.id);
  const enabled = enabledCrmLabels(company);
  return (
    <SummaryCard
      title="CRM"
      testId="hub-card-crm"
      onOpen={onOpen}
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
    >
      <Space direction="vertical" size={space.sm} style={{ width: '100%' }}>
        {connections.length === 0 ? (
          <Typography.Text>Not connected</Typography.Text>
        ) : (
          connections.map((c) => (
            <div key={c.id}>
              <Link to={`/integrations/${c.id}`}>{crmSourceLabel(c.crmSource)}</Link>{' '}
              {c.hasSecret ? (
                <Tag color="green">Credentials stored</Tag>
              ) : (
                <Tag color="warning">No credentials</Tag>
              )}
              {!crmFlagOn(company, c.crmSource) && (
                <Tag color="warning" title="This provider's flag is off, so the company can't use the connection.">
                  Flag off
                </Tag>
              )}
            </div>
          ))
        )}
        <div>
          <Typography.Text type="secondary">Enabled by flag: </Typography.Text>
          <Typography.Text>{enabled.length > 0 ? enabled.join(', ') : 'none'}</Typography.Text>
        </div>
      </Space>
    </SummaryCard>
  );
}

function DevicesCard({ companyId, onOpen }: { companyId: string; onOpen: () => void }) {
  const { data, isLoading, error, refetch } = useCompanyInstalls(companyId);
  const installs = data?.installs;
  const s = useMemo(() => summarizeInstalls(installs ?? []), [installs]);
  return (
    <SummaryCard
      title="Devices"
      testId="hub-card-devices"
      onOpen={onOpen}
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
    >
      {s.installs === 0 ? (
        <Typography.Text type="secondary">No installs reported yet</Typography.Text>
      ) : (
        <>
          <MetricRow>
            <Metric value={s.installs} label="Installs" />
            <Metric value={s.stranded} label="Stranded" tone={s.stranded > 0 ? 'danger' : undefined} />
            <Metric value={s.outdated} label="Outdated" tone={s.outdated > 0 ? 'warning' : undefined} />
          </MetricRow>
          <div>
            <Typography.Text type="secondary">Newest here: </Typography.Text>
            <Typography.Text>{s.newestVersion ?? EM_DASH}</Typography.Text>
            {data?.latestVersion && (
              <Typography.Text type="secondary"> · fleet latest {data.latestVersion}</Typography.Text>
            )}
          </div>
        </>
      )}
    </SummaryCard>
  );
}
